import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatCaseWorkflowStatus, pickActiveCaseForProfile } from '../../../utils/caseWorkflowStatus';
import { toCaseId } from '../../../services/advisorApiAdapter';

function formatOpened(iso, locale) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    const loc = locale === 'es' ? 'es-ES' : 'en-GB';
    return d.toLocaleDateString(loc, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
}

function displayFormName(raw, t) {
  const n = String(raw || '').trim().toLowerCase();
  if (n === 'regularization 2026' || n === 'extraordinary regularization 2026') {
    return t('advisorPortal.defaultCaseType');
  }
  return raw || t('advisorPortal.activeCaseApplicationFallback');
}

function legalSubtitle(active) {
  const formName = String(active?.form_name || '').toLowerCase();
  if (formName.includes('non-lucrative') || formName.includes('non lucrative')) {
    return 'Residencia no lucrativa';
  }
  return 'Residencia no lucrativa';
}

export default function ActiveCaseSummary({ customer, cases = [] }) {
  const { t, i18n } = useTranslation();
  const uiLocale = i18n.language?.split('-')[0] === 'es' ? 'es' : 'en';
  const navigate = useNavigate();
  const active = pickActiveCaseForProfile(cases);
  const hasCase = Boolean(active);

  const openCasePath = hasCase
    ? `/case/${toCaseId(active.user_id, active.session_id, active.public_case_id)}`
    : null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden h-full flex flex-col">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-100 bg-slate-50/60">
        <span className="w-3.5 h-3.5 rounded-[4px] bg-slate-400 shrink-0" />
        <h2 className="text-sm font-semibold text-slate-700">{hasCase ? active.public_case_id || toCaseId(active.user_id, active.session_id, active.public_case_id) : t('advisorPortal.activeCaseNoOpenTitle')}</h2>
      </div>
      <div className="flex-1 p-5">
        {!hasCase ? (
          <div className="flex flex-col items-center justify-center py-8 text-center h-full">
            <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 bg-slate-100 rounded-full">
              <i className="ri-folder-open-line text-xl text-slate-300" />
            </div>
            <p className="text-sm font-medium text-slate-500 mb-1">{t('advisorPortal.activeCaseNoActiveTitle')}</p>
            <p className="text-xs text-slate-400 max-w-xs mb-3">
              {t('advisorPortal.activeCaseNoActiveBody', { name: customer?.name || t('advisorPortal.profileCustomerFallback') })}
            </p>
            <button
              type="button"
              onClick={() => navigate('/customer/new')}
              className="mt-1 inline-flex items-center gap-1.5 text-sm font-medium bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line text-sm" />
              {t('advisorPortal.activeCaseOpenNewCase')}
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            <div>
              <h3 className="text-base font-semibold text-slate-900">{displayFormName(active.form_name, t)}</h3>
              <p className="text-xs text-slate-400 mt-1 italic">{legalSubtitle(active)}</p>
            </div>

            <div className="grid grid-cols-2 gap-x-8 gap-y-6">
              <div>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-0.5">
                  {t('advisorPortal.activeCaseDateOpened')}
                </p>
                <p className="text-sm text-slate-800">{formatOpened(active.created_at, uiLocale)}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-0.5">
                  {t('advisorPortal.activeCaseAssignedAdvisor')}
                </p>
                <p className="text-sm text-slate-800">{active.assigned_advisor_name || t('advisorPortal.activeCaseUnassigned')}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-0.5">
                  {t('advisorPortal.activeCaseStatusLabel')}
                </p>
                <p className="text-sm text-slate-800">{formatCaseWorkflowStatus(active.status)}</p>
              </div>
            </div>

            <div>
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">
                {t('advisorPortal.activeCaseDocuments')}
              </p>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-300 mb-2">
                Not yet uploaded
              </p>
              <div className="flex flex-wrap gap-2">
                {['Passport', 'Residency', 'Criminal', 'Asylum'].map((label) => (
                  <span key={label} className="inline-flex items-center text-xs font-medium px-3 py-1 rounded-full border border-slate-200 bg-white text-slate-400">
                    {label}
                  </span>
                ))}
              </div>
            </div>

            <button
              type="button"
              onClick={() => openCasePath && navigate(openCasePath, {
                state: {
                  userName: customer?.name || t('advisorPortal.profileCustomerFallback'),
                  publicCaseId: active?.public_case_id,
                  userId: active?.user_id,
                  sessionId: active?.session_id
                }
              })}
              className="inline-flex items-center justify-center gap-2 text-sm font-medium text-white bg-slate-900 px-5 py-2.5 rounded-xl hover:bg-slate-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-folder-open-line text-base" />
              {t('advisorPortal.activeCaseOpenCase')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
